import React, { FC, useState } from 'react';
import { Rate } from 'antd';
import { FacebookFilled, GoogleSquareFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import Slider, { Settings, CustomArrowProps } from 'react-slick';
import { Trans, useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import 'react-lazy-load-image-component/src/effects/blur.css';

import { TitleTypography } from '@design-system/components';

import { CustomerReviewProps, ICustomerReview } from '@source/components/CustomerReview/CustomerReview.types';
import { getIsAdjacentToActiveIndex } from '@design-system/components/BrowsingCard/utils';
import styled, { css } from 'styled-components';
import useMobileView from '@design-system/hooks/useMobileView';

const StyledWrapper = styled.div<{ sliderArrowTop: number }>`
  margin-bottom: 16px;
  width: 100vw;
  background: #ffede5;
  ${(props) => props.theme.typo.familyGoogle.regular};
  color: ${(props) => props.theme.color.darkBackground};
  padding-block: 45px;
  max-width: 1184px;
  margin-inline: -16px;

  @media screen and (min-width: 768px) {
    border-radius: 8px;
    margin-bottom: 40px;
    width: 100%;
    margin-inline: 0;
    padding-block: 92px;
  }

  .customer-title {
    ${(props) => props.theme.typo.familyGoogle.semiBold}
    font-size: 20px;
    text-align: center;

    margin-bottom: 20px;

    span {
      color: ${(props) => props.theme.color.primaryBase};
    }

    @media screen and (min-width: 768px) {
      font-size: 30px;
    }
  }

  .customer-desc {
    font-size: 9px;
    text-align: center;

    @media screen and (min-width: 768px) {
      font-size: 14px;
    }
  }

  .ant-divider {
    width: 80px;
    min-width: 80px;
    margin: 20px auto;
    border-top: 2px solid #ff5722;
  }

  .slider-wrapper {
    padding-inline: 30px;

    .slick-list {
      padding-top: 30px !important;
      padding-bottom: 80px !important;
    }

    .slick-next::before,
    .slick-prev::before {
      display: none !important;
    }

    .slider-arrows {
      top: ${(props) => props.sliderArrowTop}px !important;
    }

    @media screen and (min-width: 768px) {
      padding-inline: 100px;
      border-radius: 20px;
    }
  }

  .slick-slide {
    transform: translateX(0);

    & > div {
      display: flex;
      justify-content: center;

      @media screen and (min-width: 768px) {
        display: block;
      }
    }

    @media screen and (min-width: 768px) {
      transform: translateX(10px);
    }
  }
`;

const StyledCard = styled.div<{ mobileHeight: number; desktopHeight: number }>`
  width: 96% !important;
  height: ${(props) => props.mobileHeight}px;
  background: ${(props) => props.theme.color.background};
  padding: 70px 30px 30px;
  border-radius: 10px;
  display: flex !important;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  margin-top: 50px;
  align-items: center;

  .avatar {
    position: absolute;
    width: 90px;
    height: 90px;
    left: 50%;
    top: 0;
    transform: translate(-50%, -50%);

    img {
      border-radius: 100%;
      width: 100%;
      height: 100%;
    }
  }

  .card-profile {
    text-align: center;

    &__name {
      font-size: 14px;
      font-family: 'Poppins', serif;
      font-weight: 600 !important;
      margin-bottom: 22px;

      @media screen and (min-width: 768px) {
        font-size: 21px;
        margin-bottom: 10px;
      }
    }
  }

  .card-profile-desc {
    text-align: center;
    font-family: 'Poppins', serif;

    &__review {
      height: 84px;
      overflow-y: auto;
      font-size: 9px;
      text-align: center;

      @media screen and (min-width: 768px) {
        font-size: 14px;
      }

      ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 4px;
        background-color: ${(props) => props.theme.color.webkitScrollBar};
      }

      ::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background: ${(props) => props.theme.color.webkitScrollBarThumb};
        border-color: ${(props) => props.theme.color.webkitScrollBarThumb};
      }
    }

    &__source {
      margin-top: 20px;
      display: flex;
      align-items: center;
      justify-content: center;

      .anticon {
        font-size: 15px;
      }

      .facebook {
        color: ${(props) => props.theme.color.fbColor};
      }

      .google {
        color: ${(props) => props.theme.color.pureRed};
      }

      &__timeline {
        ${(props) => props.theme.typo.familyGoogle.regular};
        font-size: 10px;
        color: ${(props) => props.theme.color.webkitScrollBarThumb};
        margin-left: 7px;
      }
    }
  }

  .ant-rate {
    width: 100%;
    font-size: 14px;

    @media screen and (min-width: 768px) {
      font-size: 24px;
    }

    .ant-rate-star:not(:last-child) {
      margin-right: 10px;

      @media screen and (min-width: 768px) {
        margin-right: 8px;
      }
    }

    .anticon {
      color: ${(props) => props.theme.color.primaryBase};
    }
  }

  @media screen and (min-width: 768px) {
    height: ${(props) => props.desktopHeight}px;
    padding: 70px 50px 35px;

    .card-profile-desc {
      text-align: left;

      &__review {
        height: 126px;
      }

      &__source {
        justify-content: flex-start;
      }
    }
  }
`;

const StyledDesc = styled.div<{ isChinese: boolean }>`
  padding: 0 32px;
  text-align: center;
  font-size: 9px;
  color: ${(props) => props.theme.color.darkShapeBlue};
  ${(props) =>
    !props.isChinese &&
    css`
      max-width: 240px;
    `}

  margin: 0 auto;

  a {
    color: ${(props) => props.theme.color.lightOrange};
    ${(props) => props.theme.typo.familyGoogle.bold};
    text-decoration: underline;
  }

  @media screen and (min-width: 768px) {
    padding: 12px 0;
    font-size: 14px;
    max-width: unset;
  }
`;

const StyledArrow = styled.div`
  display: flex !important;
  width: 32px !important;
  height: 32px !important;
  justify-content: center;
  background: ${(props) => props.theme.color.primaryBase};
  align-items: center;
  border-radius: 8px;

  &:hover {
    background: ${(props) => props.theme.color.primaryBase} !important;
  }

  svg {
    color: ${(props) => props.theme.color.background};
    font-size: 16px;
    border-radius: 8px;
  }
`;

const StyledNextArrow = styled(StyledArrow)`
  right: calc(50% - 40px) !important;
  z-index: 2;
`;

const StyledPrevArrow = styled(StyledArrow)`
  left: calc(50% - 40px) !important;
  z-index: 2;
`;

const PrevArrow: FC = (props: CustomArrowProps): JSX.Element => {
  const { className, style, onClick } = props;
  return (
    <StyledPrevArrow className={`${className} slider-arrows`} style={{ ...style }} onClick={onClick}>
      <LeftOutlined />
    </StyledPrevArrow>
  );
};

const NextArrow: FC = (props: CustomArrowProps): JSX.Element => {
  const { className, style, onClick } = props;
  return (
    <StyledNextArrow className={`${className} slider-arrows`} style={{ ...style }} onClick={onClick}>
      <RightOutlined />
    </StyledNextArrow>
  );
};

const CustomerReviewMY: FC<CustomerReviewProps> = ({ content, region }) => {
  const { t } = useTranslation('ccpo');
  const isMobile = useMobileView(576);

  const lang = i18next.language;
  const isChinese = lang?.toLowerCase() === 'zh-my';

  const [activeIndex, setActiveIndex] = useState(0);

  const settings: Settings = {
    infinite: true,
    dots: false,
    speed: 500,
    slidesToShow: isMobile ? 1 : 2,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [],
    beforeChange: (oldIndex, newIndex) => {
      setActiveIndex(newIndex);
    },
  };

  const { header, customerReviews, footer } = content;

  // To align the slider arrows top position with the footer desc
  // Based on the lack of the card-profile-desc__source
  const [customerReviewItem] = customerReviews;
  const hasDataSource = customerReviewItem.type && customerReviewItem.time;
  const sliderArrowTopDesktop = hasDataSource ? 504 : 456;
  const sliderArrowTopMobile = hasDataSource ? 434 : 386;

  // To adjust mobile card height
  // Based on the lack of the card-profile-desc__source
  const mobileCardHeight = hasDataSource ? 326 : 265;
  const desktopCardHeight = hasDataSource ? 371 : 323;

  return (
    <StyledWrapper sliderArrowTop={isMobile ? sliderArrowTopMobile : sliderArrowTopDesktop}>
      <TitleTypography className="customer-title" level={2}>
        <Trans
          ns="ccpo"
          defaults={header?.title}
          components={{
            span: <span />,
            strong: <strong />,
            specificFont: <span style={{ fontFamily: 'Poppins' }} />,
            br: isMobile ? <br /> : <span />,
          }}
        />
      </TitleTypography>

      <p className="customer-desc" style={{ margin: 0 }}>
        {t(header?.description)}
      </p>

      <section className="slider-wrapper">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Slider {...settings}>
          {customerReviews.map((item: ICustomerReview, index) => (
            <StyledCard key={item?.name} mobileHeight={mobileCardHeight} desktopHeight={desktopCardHeight}>
              <figure className="avatar">
                <LazyLoadImage
                  effect="blur"
                  alt="customer"
                  src={item.avatar.replace(
                    'https://cdn.carro.co/',
                    `https://cdn.carro.co/tr:n-carro_certified_page_user_review_avatar/`,
                  )}
                  visibleByDefault={getIsAdjacentToActiveIndex(index, activeIndex, customerReviews.length)}
                />
              </figure>
              <div className="card-profile">
                <h3 className="card-profile__name">{item.name}</h3>
                <div className="card-profile-desc">
                  <div className="card-profile-desc__review">{item.review}</div>
                  {item.type && item.time && (
                    <div className="card-profile-desc__source">
                      <span>
                        {item.type === 'facebook' && <FacebookFilled className="facebook" />}
                        {item.type === 'google' && <GoogleSquareFilled className="google" />}
                      </span>
                      <span className="card-profile-desc__source__timeline">{item.time}</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Rate disabled defaultValue={item.star} />
              </div>
            </StyledCard>
          ))}
        </Slider>
      </section>

      <StyledDesc isChinese={isChinese}>
        <Trans defaults={t(footer?.preText)} ns="ccpo" />
        <a href={footer?.anchor?.link} target="_blank" rel="noreferrer">
          {t(footer?.anchor?.text)}
        </a>
        <Trans ns="ccpo" defaults={t(footer?.[isMobile && region === 'hk' ? 'postTextMobile' : 'postText'])} />
      </StyledDesc>
    </StyledWrapper>
  );
};

export default CustomerReviewMY;
